import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  ShieldCheck, ShoppingBag, Clock, Key, CreditCard, 
  Activity, Smartphone 
} from 'lucide-react';
import { Link } from 'react-router-dom';

const StatCard = ({ title, value, icon: Icon, color, bg }) => (
  <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
    <div>
      <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
      <h3 className="text-2xl font-bold text-slate-800">{value}</h3>
    </div>
    <div className={`p-3 rounded-xl ${bg}`}>
      <Icon className={`w-6 h-6 ${color}`} />
    </div>
  </div>
);

const UserDashboard = () => {
  // Fetch Stats
  const { data: stats, isLoading } = useQuery({
    queryKey: ['userStats'],
    queryFn: () => axiosClient.get('/account/stats')
  });

  // Fetch Recent Licenses
  const { data: licenses = [] } = useQuery({
    queryKey: ['userLicenses'],
    queryFn: () => axiosClient.get('/account/licenses')
  });

  if (isLoading) return <div className="p-10 text-center text-slate-400">Loading your secure portal...</div>;

  return (
    <div className="space-y-8 animate-fade-in pb-10">
      
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-slate-900 to-blue-900 rounded-2xl p-8 text-white relative overflow-hidden shadow-lg">
         <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 opacity-10 rounded-full blur-3xl -mr-16 -mt-16"></div>
         <div className="relative z-10">
            <h1 className="text-2xl font-bold mb-2">Welcome to AIRS Defense Grid</h1>
            <p className="text-slate-300 max-w-xl">
               Your systems are protected by autonomous AI. Manage your licenses and billing below.
            </p>
            <div className="mt-6 flex gap-3">
               <button className="bg-white text-blue-900 px-5 py-2 rounded-lg font-bold text-sm hover:bg-slate-100 transition-colors">
                  Download Agent
               </button>
               <Link to="/portal/orders" className="px-5 py-2 rounded-lg font-bold text-sm border border-white/20 hover:bg-white/10 transition-colors">
                  View Billing
               </Link>
            </div>
         </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Active Licenses" value={stats?.active_licenses || 0} icon={ShieldCheck} color="text-green-600" bg="bg-green-50" />
        <StatCard title="Total Keys" value={stats?.total_licenses || 0} icon={Key} color="text-blue-600" bg="bg-blue-50" />
        <StatCard title="Orders Placed" value={stats?.total_orders || 0} icon={ShoppingBag} color="text-purple-600" bg="bg-purple-50" />
        <StatCard title="Total Spend" value={`$${stats?.total_spend || 0}`} icon={CreditCard} color="text-amber-600" bg="bg-amber-50" />
      </div>

      {/* Split grid layout for Licenses & Mobile Onboarding */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
         {/* Recent Licenses Table */}
         <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden lg:col-span-2">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
               <h3 className="font-bold text-slate-800">Your Active Protections</h3>
               <Link to="/portal/licenses" className="text-sm text-blue-600 hover:underline">View All</Link>
            </div>
            <table className="w-full text-left">
               <thead className="bg-slate-50 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase">
                  <tr>
                     <th className="px-6 py-4">Package</th>
                     <th className="px-6 py-4">License Key</th>
                     <th className="px-6 py-4">Status</th>
                     <th className="px-6 py-4">Expires</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-slate-50">
                  {licenses.length === 0 ? (
                     <tr><td colSpan="4" className="p-8 text-center text-slate-400">No licenses found. Buy a plan to get started.</td></tr>
                  ) : (
                     licenses.slice(0, 5).map((l, i) => (
                        <tr key={i} className="hover:bg-slate-50 transition-colors">
                           <td className="px-6 py-4 font-bold text-slate-700">{l.package_name || 'Custom Plan'}</td>
                           <td className="px-6 py-4 font-mono text-xs text-slate-600 bg-slate-100 rounded w-fit px-2 py-1 select-all">
                              {l.license_key}
                           </td>
                           <td className="px-6 py-4">
                              {l.activated_at ? (
                                 <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                                    <Activity className="w-3 h-3 mr-1" /> Active
                                 </span>
                              ) : (
                                 <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                                    <Clock className="w-3 h-3 mr-1" /> Pending
                                 </span>
                              )}
                           </td>
                           <td className="px-6 py-4 text-sm text-slate-500">
                              {l.expires_at ? new Date(l.expires_at).toLocaleDateString() : 'Lifetime'}
                           </td>
                        </tr>
                     ))
                  )}
               </tbody>
            </table>
         </div>

         {/* Mobile Onboarding On-Premise Installer Card */}
         <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex flex-col justify-between lg:col-span-1">
            <div>
               <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center mb-4 border border-green-100">
                  <Smartphone className="w-5 h-5 text-green-600" />
               </div>
               <h3 className="font-bold text-slate-800 text-base">AIRS Mobile Shield</h3>
               <p className="text-slate-500 text-xs mt-1.5 leading-relaxed">
                  Secure your Android workspace against zero-day intercept vectors, phishing SMS links, and high-severity overlay tapjacking attempts.
               </p>
            </div>
            <div className="mt-5">
               <a 
                  href="https://api.airssecurity.com/downloads/airs-security-mobile.apk" 
                  download
                  className="w-full py-2.5 bg-green-50 hover:bg-green-100 text-green-700 font-bold rounded-lg text-xs text-center block transition-colors border border-green-100"
               >
                  Install Mobile Shield (APK)
               </a>
            </div>
         </div>
      </div>

    </div>
  );
};

export default UserDashboard;